import React, { Fragment } from 'react';
import API from '../../../../utils/API';
import SelectField from '../../../../components/inputs/selectField';
import FieldModal from '../../../../components/modals/fieldModal';
import { withRouter } from 'react-router-dom';

class SedgwickConfigModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedSourceForm: null,
            availableForms: [],
            loading: true,
            fieldRemappings: {},
            requiredRemappings: [],
            submitting: false
        };
    }

    componentDidMount() {
        this.fetchSedgwickEnabledForms();
    }

    fetchSedgwickEnabledForms = async () => {
        const { eventId, orgId, currentFormId } = this.props;
        
        try {
            const response = await API().get(`Organizations/${orgId}/events/${eventId}/forms`);
            
            if (response.data) {
                // Filter for forms with Sedgwick enabled, excluding current form
                const sedgwickForms = response.data.forms.filter(form => 
                    form._id !== currentFormId &&
                    form.integrationSettings?.sedgwick?.enabled === true
                );

                console.log('Sedgwick-enabled forms:', sedgwickForms);
                
                const formOptions = sedgwickForms.map(form => ({
                    label: form.name,
                    value: form._id,
                    integrationSettings: form.integrationSettings
                }));
                
                this.setState({ 
                    availableForms: formOptions,
                    loading: false 
                });
            }
        } catch (error) {
            console.error('Error fetching Sedgwick-enabled forms:', error);
            this.setState({ loading: false });
        }
    };

    handleSourceFormChange = (selectedOption) => {
        const { formFieldOptions } = this.props;
        const sourceConfig = selectedOption.integrationSettings.sedgwick.registrationPayload;
        
        // Identify fields that need remapping (form fields only)
        const requiredRemappings = [];
        const fieldRemappings = {};
        
        Object.keys(sourceConfig).forEach(key => {
            if (key === 'ExtendedProperties') {
                // Handle nested ExtendedProperties
                Object.keys(sourceConfig.ExtendedProperties).forEach(nestedKey => {
                    const fieldValue = sourceConfig.ExtendedProperties[nestedKey];
                    if (fieldValue && fieldValue.startsWith('form.')) {
                        const fieldPath = `ExtendedProperties.${nestedKey}`;
                        requiredRemappings.push({
                            label: nestedKey.replace(/([A-Z])/g, ' $1').trim(),
                            path: fieldPath,
                            originalValue: fieldValue
                        });
                        // Check if the field exists in current form
                        const existsInCurrentForm = formFieldOptions.some(opt => opt.value === fieldValue);
                        fieldRemappings[fieldPath] = existsInCurrentForm ? fieldValue : null;
                    }
                });
            } else {
                const fieldValue = sourceConfig[key];
                if (fieldValue && fieldValue.startsWith('form.')) {
                    requiredRemappings.push({
                        label: key.replace(/([A-Z])/g, ' $1').trim(),
                        path: key,
                        originalValue: fieldValue
                    });
                    // Check if the field exists in current form
                    const existsInCurrentForm = formFieldOptions.some(opt => opt.value === fieldValue);
                    fieldRemappings[key] = existsInCurrentForm ? fieldValue : null;
                }
            }
        });
        
        this.setState({
            selectedSourceForm: selectedOption,
            requiredRemappings,
            fieldRemappings
        });
    };

    handleFieldRemapping = (fieldPath, newValue) => {
        this.setState(prevState => ({
            fieldRemappings: {
                ...prevState.fieldRemappings,
                [fieldPath]: newValue
            }
        }));
    };

    canApplyConfig = () => {
        const { selectedSourceForm } = this.state;
        
        // Allow applying configuration as long as a source form is selected
        // Field mappings are optional
        return selectedSourceForm !== null;
    };

    applyConfiguration = () => {
        const { selectedSourceForm, fieldRemappings } = this.state;
        const { onApply } = this.props;
        
        if (!selectedSourceForm) return;
        
        this.setState({ submitting: true });
        
        const sourceConfig = selectedSourceForm.integrationSettings.sedgwick.registrationPayload;
        const newConfig = { ...sourceConfig };
        
        // Apply remappings
        Object.keys(fieldRemappings).forEach(fieldPath => {
            const pathParts = fieldPath.split('.');
            if (pathParts.length > 1) {
                // Handle nested properties
                if (!newConfig[pathParts[0]]) {
                    newConfig[pathParts[0]] = {};
                }
                newConfig[pathParts[0]][pathParts[1]] = fieldRemappings[fieldPath];
            } else {
                newConfig[fieldPath] = fieldRemappings[fieldPath];
            }
        });
        
        onApply(newConfig);
        this.setState({ submitting: false });
    };

    skipConfiguration = () => {
        const { onSkip } = this.props;
        onSkip();
    };

    render() {
        const { isOpen, toggle, formFieldOptions } = this.props;
        const { 
            availableForms, 
            selectedSourceForm, 
            loading, 
            requiredRemappings, 
            fieldRemappings,
            submitting 
        } = this.state;

        return (
            <FieldModal
                size="medium"
                isOpen={isOpen}
                modalTitle="Configure Sedgwick Integration"
                bodyHeaderText={
                    availableForms.length > 0 
                        ? 'Would you like to use the configuration from another form?' 
                        : 'No other forms with Sedgwick integration found.'
                }
                bodyContent={() => {
                    if (loading) {
                        return <div className="text-center p-20">Loading forms...</div>;
                    }
                    
                    if (availableForms.length === 0) {
                        return (
                            <div className="text-center p-20">
                                <p>You can configure Sedgwick integration manually in the form settings.</p>
                            </div>
                        );
                    }
                    
                    return (
                        <div>
                            <SelectField
                                label="Select Form to Copy Configuration From"
                                options={availableForms}
                                value={selectedSourceForm}
                                onChange={this.handleSourceFormChange}
                                placeholder="Choose a form..."
                            />
                            
                            {selectedSourceForm && requiredRemappings.length > 0 && (
                                <div className="mt-20">
                                    <h4 className="formFieldLabel mb-10">
                                        The following form fields can be reconfigured (optional):
                                    </h4>
                                    <p className="mb-15" style={{ fontSize: '0.9em', color: '#666' }}>
                                        You can optionally map these fields to your current form's fields. Attendee and custom fields will be copied automatically. Unmapped fields will be left empty.
                                    </p>
                                    
                                    {requiredRemappings.map((field, index) => {
                                        const currentValue = fieldRemappings[field.path];
                                        const isValid = currentValue !== null && currentValue !== undefined;
                                        
                                        return (
                                            <div key={index} className="mb-15">
                                                <SelectField
                                                    label={field.label}
                                                    options={formFieldOptions.filter(opt => opt.type === 'form')}
                                                    value={formFieldOptions.find(opt => opt.value === currentValue)}
                                                    onChange={(e) => this.handleFieldRemapping(field.path, e.value)}
                                                    placeholder="Select a field (optional)..."
                                                    required={false}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            
                            {selectedSourceForm && requiredRemappings.length === 0 && (
                                <div className="mt-20 p-15" style={{ background: '#e8f5e9', borderRadius: '4px' }}>
                                    <p style={{ margin: 0, color: '#2e7d32' }}>
                                        ✓ All fields from the selected form can be copied directly. No remapping needed.
                                    </p>
                                </div>
                            )}
                        </div>
                    );
                }}
                toggle={toggle}
                actionButtonLabel={
                    availableForms.length === 0 
                        ? 'Configure Manually' 
                        : submitting 
                            ? 'Applying...' 
                            : selectedSourceForm 
                                ? 'Apply Configuration' 
                                : 'Skip & Configure Manually'
                }
                actionButton={
                    availableForms.length === 0 || !selectedSourceForm
                        ? this.skipConfiguration
                        : this.applyConfiguration
                }
                actionButtonDisabled={submitting}
                cancelButtonLabel="Cancel"
            />
        );
    }
}

export default withRouter(SedgwickConfigModal);
